<template>
  <div class="px-5 sm:px-6 lg:px-10 py-6">
    <h1 class="text-[26px] sm:text-[24px] leading-7 font-bold text-gray-900 mb-6">Checks</h1>

    <div v-if="!can.view" class="mt-10"></div>

    <template v-else>
      <div class="mb-12 max-w-md">
        <label class="block text-sm font-semibold text-gray-800 mb-2">Company</label>
        <select
          v-model="selectedCompany"
          class="w-full h-10 rounded-xl border border-gray-300 px-3 bg-white focus:outline-none"
        >
          <option disabled value="">Select company</option>
          <option v-for="c in companies" :key="c.id" :value="c.id">{{ c.name }}</option>
        </select>
      </div>

      <div
        v-if="selectedCompany && can.viewCheck"
        ref="cardRef"
        class="relative bg-white border border-gray-200 rounded-2xl shadow-sm p-0 overflow-hidden"
      >
        <DynamicTable
          :wrapperClass="'checks-table'"
          :rows="rows"
          :columns="columns"
          :rowsPerPage="50"
          :rowsPerPageOptions="[10, 20, 50]"
          :rowClass="rowClass"
          :showHeader="true"
          searchPlaceholder="Search"
          :showAddButton="can.edit"
          addButtonLabel="New check"
          :showEdit="false"
          :showView="false"
          :showDelete="false"
          @add="onAdd"
        >
          <!-- Created By (read-only) -->
          <template v-slot:[slCreated]="{ value }">
            <span class="truncate text-gray-700">{{ value || '—' }}</span>
          </template>

          <!-- Approved By → dots (open modal) or dashes -->
          <template v-slot:[slApproved]="{ row }">
            <div class="flex items-center gap-1">
              <button
                v-if="row.approvers && row.approvers.length"
                class="inline-flex w-6 h-6 rounded hover:bg-gray-100"
                @click="openApprovers(row)"
                title="View approvers"
              >
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="12" cy="5" r="1.5" />
                  <circle cx="12" cy="12" r="1.5" />
                  <circle cx="12" cy="19" r="1.5" />
                </svg>
              </button>
              <span v-else class="text-gray-400 select-none">-- --</span>
            </div>
          </template>

          <template v-slot:[slTransfer]="{ value }">
            <div class="text-center">
              <span
                :class="value ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'"
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
              >
                {{ value ? 'Yes' : 'No' }}
              </span>
            </div>
          </template>

          <template v-slot:[slActive]="{ value }">
            <div class="text-center">{{ value }}</div>
          </template>

          <template v-slot:[slId]="{ value }">
            <span class="tabular-nums">{{ value }}</span>
          </template>

          <template v-slot:[slAmount]="{ value }">
            <div class="tabular-nums w-full">{{ value }}</div>
          </template>

          <template #action="{ row }">
            <div class="relative flex items-center justify-center">
              <button
                class="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-gray-100"
                @click="openActionsMenu($event, row)"
                title="Actions"
              >
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="12" cy="5" r="1.5" />
                  <circle cx="12" cy="12" r="1.5" />
                  <circle cx="12" cy="19" r="1.5" />
                </svg>
              </button>
            </div>
          </template>
        </DynamicTable>

        <div
          v-for="ov in inactiveOverlays"
          :key="ov.key"
          class="pointer-events-none absolute inset-x-0 z-10 flex items-center justify-center font-extrabold text-2xl text-gray-500 select-none"
          :style="{
            top: ov.top + 'px',
            height: ov.height + 'px',
            background: 'rgba(229,231,235,0.5)',
          }"
        >
          Inactive
        </div>
      </div>

      <!-- Approvers modal (read-only list of who approved) -->
      <div
        v-if="approversModal.open"
        class="fixed inset-0 z-[9800] bg-black/50 flex items-center justify-center"
      >
        <div class="bg-white rounded-2xl shadow-lg w-[320px] p-6 relative">
          <button
            class="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
            @click="closeApprovers"
            title="Close"
          >
            ✕
          </button>
          <div class="space-y-5 text-center text-[18px] font-semibold text-gray-800">
            <p v-if="!approversModal.names.length" class="text-sm text-gray-400 font-normal">
              No approvers yet.
            </p>
            <div v-for="n in approversModal.names" :key="n">
              {{ n }}
            </div>
          </div>
        </div>
      </div>

      <teleport to="body">
        <div
          v-if="actionsMenu.open"
          class="fixed z-[9999]"
          :style="{ top: actionsMenu.y + 'px', left: actionsMenu.x + 'px' }"
          @click.stop
          ref="menuRef"
        >
          <div class="w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-2">
            <button
              v-for="(key, idx) in visibleActions"
              :key="idx"
              class="w-full text-left px-4 py-2 text-gray-800 hover:bg-gray-100 flex items-center gap-2"
              @click="handleAction(key, actionsMenu.row)"
            >
              <svg v-if="key === 'approve'" class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 16.17 4.83 12 3.41 13.41 9 19l12-12-1.41-1.41z" />
              </svg>
              <svg
                v-else-if="key === 'reject'"
                class="w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
              <svg
                v-else-if="key === 'edit'"
                class="w-4 h-4"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path
                  d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25Zm14.16-9.91 1.5-1.5a1 1 0 1 0-1.41-1.41l-1.5 1.5 1.41 1.41Z"
                />
              </svg>
              <svg
                v-else-if="key === 'view'"
                class="w-4 h-4"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path
                  d="M12 5C5 5 2 12 2 12s3 7 10 7 10-7 10-7-3-7-10-7Zm0 11a4 4 0 1 1 0-8 4 4 0 0 1 0 8Z"
                />
              </svg>
              <svg
                v-else-if="key === 'delete'"
                class="w-4 h-4"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M6 19a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7H6v12ZM8 4h8l1 1h3v2H4V5h3l1-1Z" />
              </svg>
              <svg
                v-else-if="key === 'print'"
                class="w-4 h-4"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M17 8V3H7v5h10Zm1 2h2a2 2 0 0 1 2 2v5h-5v3H7v-3H2v-5a2 2 0 0 1 2-2h2" />
              </svg>
              <svg
                v-else-if="key === 'transfer'"
                class="w-4 h-4"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M7 7h7V4l5 5-5 5v-3H7V7Zm10 10H5V8H3v11h14v3l5-5-5-5v3Z" />
              </svg>
              <svg
                v-else-if="key === 'inactive'"
                class="w-4 h-4"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M4 4h16v2H4zm0 7h16v2H4zm0 7h16v2H4z" />
              </svg>
              <span class="text-[14px]">{{ labels[key] }}</span>
            </button>
          </div>
        </div>
        <div v-if="actionsMenu.open" class="fixed inset-0 z-[9998]" @click="closeActions"></div>
      </teleport>

      <DeleteModal
        v-model="deleteState.open"
        :title="deleteTitle"
        :message="deleteMessage"
        @confirm="confirmDelete"
        @cancel="cancelDelete"
      />
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue'
import { useRouter, onBeforeRouteLeave } from 'vue-router'
import api from '@/api'
import DynamicTable from '@/components/DynamicTable.vue'
import DeleteModal from '@/components/DeleteModal.vue'
import { loadMyPermissions, hasAny } from '@/utils/permissions'
import Swal from 'sweetalert2'

const router = useRouter()
const ROUTE_NAME = 'CheckUpsertView'
const ROUTE_PRINT_NAME = 'CheckPrint'

/* ===== Permissions ===== */
const can = ref({
  view: false,
  viewCheck: false,
  actionView: false,
  approve: false,
  reject: false,
  edit: false,
  delete: false,
  print: false,
  inactive: false,
})

/* ===== Companies ===== */
const companies = ref([])
const selectedCompany = ref('')

const authConfig = computed(() => {
  const token = localStorage.getItem('token')
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} }
})

async function fetchCompanies() {
  try {
    const res = await api.get('/accounting/companies', authConfig.value)
    const data = Array.isArray(res.data) ? res.data : res.data?.data || []
    companies.value = data.map((c) => ({ id: c.id, name: c.name }))

    const savedId = sessionStorage.getItem('checks_selected_company_id')
    if (savedId && companies.value.some((c) => String(c.id) === String(savedId))) {
      selectedCompany.value = savedId
    }
  } catch (e) {
    console.error(e)
  }
}

watch(selectedCompany, (val) => {
  if (val) {
    sessionStorage.setItem('checks_selected_company_id', String(val))
    const co = companies.value.find((c) => String(c.id) === String(val))
    if (co) sessionStorage.setItem('checks_selected_company_name', co.name)
    fetchChecks()
  }
})

onBeforeRouteLeave((to) => {
  const checksRoutes = ['Checks', 'CheckUpsertView', 'CheckPrint']
  if (!checksRoutes.includes(to.name)) {
    sessionStorage.removeItem('checks_selected_company_id')
    sessionStorage.removeItem('checks_selected_company_name')
  }
})

/* ===== Fetch Checks from API (fetch ALL pages, table paginates client-side) ===== */
async function fetchChecks() {
  if (!selectedCompany.value) return
  try {
    const perPage = 100
    const maxPages = 1000 // hard safety cap to prevent any runaway loop
    let page = 1
    const all = []

    while (page <= maxPages) {
      const res = await api.get('/accounting/checks', {
        ...authConfig.value,
        params: { company_id: selectedCompany.value, page, per_page: perPage },
      })

      const body = res.data
      const chunk = Array.isArray(body) ? body : body?.data || []
      all.push(...chunk)

      const lastPage = body?.last_page ?? body?.meta?.last_page ?? null

      if (lastPage != null) {
        if (page >= lastPage) break
      } else if (chunk.length < perPage) {
        break
      }

      page += 1
    }

    rows.value = all.map((c) => mapCheckRow(c))
    nextTick(measureInactiveOverlays)
  } catch (e) {
    console.error(e)
  }
}

function mapCheckRow(c) {
  const isInactive = c.status === 'inactive'
  const isApproved = c.status === 'approved'
  const isRelayed  = c.status === 'relayed'
  const isTransferred = Number(c.bank_transfer) === 1

  const rawApprovers = c.approved_by ?? c.approvers ?? c.approved_by_name ?? []
  const approvers = Array.isArray(rawApprovers)
    ? rawApprovers.filter(Boolean)
    : (rawApprovers ? [rawApprovers] : [])

  return {
    id:             c.company_sequence ?? c.id,
    dbId:           c.id,
    paymentTo:      c.payment_for_name || '',
    paymentMethod:  c.payment_method    || '',
    paymentNo:      c.check_id          || '---',
    date:           c.date              || '',
    amount:         c.total != null ? parseFloat(c.total) : '',
    // who created the check (fallback to legacy waiting_by_name until backend adds created_by)
    createdBy:      c.created_by || c.created_by_name || c.waiting_by_name || '',
    // names shown in the "Approved By" modal
    approvers,
    // has the current logged-in user already approved?
    approvedByMe:   c.approved_by_me != null ? !!c.approved_by_me : false,
    transferToBank: isTransferred,
    active:         !isInactive,
    activeText:     isInactive ? 'No' : 'Yes',
    approved:       isApproved || isRelayed,
    transferred:    isTransferred,
    canEdit:        true,
    _raw:           c,
  }
}

/* ===== Table ===== */
const columns = [
  { key: 'id', label: 'ID', width: '50px' },
  { key: 'paymentTo', label: 'Payment To', width: '220px' },
  { key: 'paymentMethod', label: 'Payment Method', width: '115px' },
  { key: 'paymentNo', label: 'Check No.', width: '100px' },
  { key: 'date', label: 'Date', width: '85px' },
  { key: 'amount', label: 'Amount', width: '75px' },
  { key: 'createdBy', label: 'Created By', width: '135px' },
  { key: 'approvedBy', label: 'Approved By', width: '135px' },
  { key: 'transferToBank', label: 'Transfer', width: '100px' ,align: 'center' },
  { key: 'activeText', label: 'Active', width: '75px' , align: 'center' },
  { key: 'action', label: 'Action', width: '150px' },
]

const rows = ref([])

const slCreated = 'cell:createdBy'
const slApproved = 'cell:approvedBy'
const slTransfer = 'cell:transferToBank'
const slActive = 'cell:activeText'
const slId = 'cell:id'
const slAmount = 'cell:amount'

const rowClass = (row) => (row.active ? '' : 'inactive-row')

const actionsMenu = ref({ open: false, x: 0, y: 0, row: null, triggerRect: null })
const menuRef = ref(null)
const labels = {
  approve: 'Approve',
  reject: 'Reject',
  edit: 'Edit',
  view: 'View',
  delete: 'Delete',
  print: 'Print',
  transfer: 'Transfer',
  inactive: 'Inactive',
}

const visibleActions = computed(() => {
  const r = actionsMenu.value.row
  if (!r) return []

  const all = []

  if (!r.approved) {
    // hide Approve once the current user has already approved
    if (can.value.approve && !r.approvedByMe) all.push('approve')
    if (can.value.reject) all.push('reject')
    if (can.value.delete) all.push('delete')
    if (can.value.inactive) all.push('inactive')
  }

  if (!r.transferred && can.value.edit) {
    all.push('edit')
  }

  if (can.value.actionView) all.push('view')
  if (can.value.print) all.push('print')

  all.push('transfer')

  return all
})

function closeActions() {
  actionsMenu.value = { open: false, x: 0, y: 0, row: null, triggerRect: null }
}

function goToUpsert(mode, row) {
  const co = companies.value.find((c) => String(c.id) === String(selectedCompany.value))
  router.push({
    name: ROUTE_NAME,
    state: {
      checkCompanyId: String(selectedCompany.value),
      checkCompanyName: co?.name || sessionStorage.getItem('checks_selected_company_name') || '',
    },
    query: { mode, ...(row?.dbId ? { id: row.dbId } : {}) },
  })
}

const deleteState = ref({ open: false, index: -1, rowId: null })
const deleteTitle = ref('Are you sure?')
const deleteMessage = ref('Delete this item?')

function openActionsMenu(e, row) {
  if (isRowInactive(row)) return
  const rect = e.currentTarget.getBoundingClientRect()
  actionsMenu.value = { open: true, x: rect.left, y: rect.bottom + 6, row, triggerRect: rect }
  nextTick(adjustMenuPosition)
}

async function handleAction(key, row) {
  if (isRowInactive(row)) {
    closeActions()
    return
  }

  if (key === 'approve' && !can.value.approve) { closeActions(); return }
  if (key === 'reject'  && !can.value.reject)  { closeActions(); return }
  if (key === 'edit'    && !can.value.edit)     { closeActions(); return }
  if (key === 'view'    && !can.value.actionView) { closeActions(); return }
  if (key === 'delete'  && !can.value.delete)   { closeActions(); return }
  if (key === 'print'   && !can.value.print)    { closeActions(); return }
  if (key === 'inactive'&& !can.value.inactive) { closeActions(); return }

  if (key === 'approve') {
    try {
      await api.post(`/accounting/checks/${row.dbId}/approve`, {}, authConfig.value)
      await fetchChecks()
    } catch (e) {
      console.error(e)
    }
    closeActions()
    return
  }

  if (key === 'reject') {
    try {
      await api.post(`/accounting/checks/${row.dbId}/reject`, {}, authConfig.value)
      await fetchChecks()
    } catch (e) {
      console.error(e)
    }
    closeActions()
    return
  }

if (key === 'transfer') {
    closeActions()
    const confirmResult = await Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to transfer this check to the bank?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, transfer it!',
      cancelButtonText: 'Cancel',
      customClass: {
        confirmButton: '!px-4 !py-2 !mx-2 !rounded-xl !text-white !bg-[#5B9AC5] hover:!bg-[#4a83ab] !transition-colors !font-medium !text-sm !focus:outline-none !inline-block !visible',
        cancelButton: '!px-4 !py-2 !mx-2 !rounded-xl !text-gray-700 !bg-gray-100 hover:!bg-gray-200 !transition-colors !font-medium !text-sm !focus:outline-none !inline-block !visible'
      },
      buttonsStyling: false
    })

    if (!confirmResult.isConfirmed) return

    Swal.fire({
      title: 'Transferring...',
      text: 'Please wait while transferring to bank.',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading()
      }
    })

    try {
      const res = await api.post(`/accounting/checks/${row.dbId}/bank-transfer`, {}, authConfig.value)

      await Swal.fire({
        icon: 'success',
        title: res?.data?.message || 'Transferred to bank successfully',
        toast: true,
        position: 'top',
        timer: 1400,
        showConfirmButton: false,
      })
      await fetchChecks()
    } catch (e) {
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: e?.response?.data?.message || e?.message || 'Failed to transfer to bank.',
        toast: true,
        position: 'top',
        timer: 2200,
        showConfirmButton: false,
      })
    }
    return
  }

  if (key === 'inactive') {
    try {
      await api.post(`/accounting/checks/${row.dbId}/inactive`, {}, authConfig.value)
      await fetchChecks()
    } catch (e) {
      console.error(e)
    }
    closeActions()
    nextTick(() => {
      measureInactiveOverlays()
      adjustMenuPosition()
    })
    return
  }

  if (key === 'edit')  return goToUpsert('edit', row)
  if (key === 'view')  return goToUpsert('view', row)

  if (key === 'print') {
    closeActions()
    return router.push({ name: ROUTE_PRINT_NAME, query: { id: row?.dbId ?? '' } })
  }

  if (key === 'delete') {
    const idx = rows.value.indexOf(row)
    if (idx > -1) {
      deleteState.value = { open: true, index: idx, rowId: row.dbId }
      closeActions()
      return
    }
  }

  nextTick(() => {
    measureInactiveOverlays()
    adjustMenuPosition()
  })
  closeActions()
}

async function confirmDelete() {
  const { index, rowId } = deleteState.value
  if (rowId) {
    try {
      await api.delete(`/accounting/checks/${rowId}`, authConfig.value)
      await fetchChecks()
    } catch (e) {
      console.error(e)
      if (index > -1) rows.value.splice(index, 1)
      nextTick(measureInactiveOverlays)
    }
  }
  deleteState.value = { open: false, index: -1, rowId: null }
}

function cancelDelete() {
  deleteState.value = { open: false, index: -1, rowId: null }
}

function onAdd() {
  if (!can.value.edit) return
  goToUpsert('add')
}

/* ===== Approvers modal ===== */
const approversModal = ref({ open: false, names: [] })

function openApprovers(row) {
  approversModal.value = { open: true, names: row.approvers || [] }
}

function closeApprovers() {
  approversModal.value = { open: false, names: [] }
}

const cardRef = ref(null)
const inactiveOverlays = ref([])

function measureInactiveOverlays() {
  const el = cardRef.value
  if (!el) return
  const wrapRect = el.getBoundingClientRect()
  const trs = el.querySelectorAll('tbody tr.inactive-row')
  const arr = []
  trs.forEach((tr, i) => {
    const r = tr.getBoundingClientRect()
    arr.push({ key: `${i}-${r.top}`, top: r.top - wrapRect.top, height: r.height })
  })
  inactiveOverlays.value = arr
}

function adjustMenuPosition() {
  const m = menuRef.value
  const s = actionsMenu.value
  if (!m || !s.open) return
  const pad = 8,
    mw = m.offsetWidth,
    mh = m.offsetHeight,
    vw = window.innerWidth,
    vh = window.innerHeight
  let x = s.x,
    y = s.y
  if (x + mw > vw - pad) x = vw - mw - pad
  if (x < pad) x = pad
  if (y + mh > vh - pad) y = Math.max(pad, (s.triggerRect?.top ?? y) - mh - 6)
  actionsMenu.value.x = x
  actionsMenu.value.y = y
}

function isRowInactive(row) {
  return row && (row.active === false || row.activeText === 'No')
}
function onKey(e) {
  if (e.key === 'Escape') {
    closeActions()
    closeApprovers()
  }
}
function reflow() {
  adjustMenuPosition()
  measureInactiveOverlays()
}

onMounted(async () => {
  await loadMyPermissions(false)
  can.value.view       = hasAny(['check.view'])
  can.value.viewCheck  = hasAny(['check.check.view'])
  can.value.actionView = hasAny(['check.check.actions.view'])
  can.value.approve    = hasAny(['check.check.actions.approve'])
  can.value.reject     = hasAny(['check.check.actions.reject'])
  can.value.edit       = hasAny(['check.check.actions.edit'])
  can.value.delete     = hasAny(['check.check.actions.delete'])
  can.value.print      = hasAny(['check.check.actions.print'])
  can.value.inactive   = hasAny(['check.check.actions.inactive'])

  await fetchCompanies()

  if (selectedCompany.value) {
    await fetchChecks()
  }

  measureInactiveOverlays()
  window.addEventListener('keydown', onKey)
  window.addEventListener('resize', reflow)
  window.addEventListener('scroll', reflow, true)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKey)
  window.removeEventListener('resize', reflow)
  window.removeEventListener('scroll', reflow, true)
})

watch([rows, selectedCompany], () => nextTick(measureInactiveOverlays), { deep: true })
</script>
<style scoped>
:deep(.checks-table table tbody td) {
  height: 42px !important;
  padding-top: 10px !important;
  padding-bottom: 10px !important;
}
:deep(.checks-table th),
:deep(.checks-table td) {
  white-space: nowrap;
}
</style>
